
import { useState, useEffect } from 'react';

interface ProcessStep {
  id: number;
  step: string;
  title: string;
  description: string;
  icon: string;
  details: string[];
}

export default function ProcessSection() {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const processSteps: ProcessStep[] = [
    {
      id: 1,
      step: '01',
      title: 'Keşif & Analiz',
      description: 'Projenizi detaylı bir şekilde analiz ediyor, ihtiyaçlarınızı belirliyoruz.',
      icon: 'ri-search-line',
      details: [
        'İş gereksinimlerinin analizi',
        'Hedef kitle araştırması',
        'Rekabet analizi',
        'Teknik gereksinimler'
      ]
    },
    {
      id: 2,
      step: '02',
      title: 'Planlama & Strateji',
      description: 'Analiz sonuçlarına göre detaylı proje planı ve strateji oluşturuyoruz.',
      icon: 'ri-mind-map',
      details: [
        'Proje yol haritası',
        'Tasarım stratejisi',
        'İçerik planlaması',
        'Zaman çizelgesi'
      ]
    },
    {
      id: 3,
      step: '03',
      title: 'Tasarım & Prototyping',
      description: 'Kullanıcı deneyimini merkeze alan tasarımlar ve prototipler oluşturuyoruz.',
      icon: 'ri-palette-line',
      details: [
        'Wireframe oluşturma',
        'UI/UX tasarımı',
        'İnteraktif prototipler',
        'Kullanılabilirlik testleri'
      ]
    },
    {
      id: 4,
      step: '04',
      title: 'Geliştirme & Kodlama',
      description: 'Modern teknolojilerle yüksek kaliteli, performanslı çözümler geliştiriyoruz.',
      icon: 'ri-code-s-slash-line',
      details: [
        'Frontend geliştirme',
        'Backend entegrasyonu',
        'Responsive tasarım',
        'Performans optimizasyonu'
      ]
    },
    {
      id: 5,
      step: '05',
      title: 'Test & Optimizasyon',
      description: 'Kapsamlı testler yaparak projenizi mükemmel hale getiriyoruz.',
      icon: 'ri-bug-line',
      details: [
        'Fonksiyonel testler',
        'Performans testleri',
        'Güvenlik testleri',
        'Cross-browser uyumluluk'
      ]
    },
    {
      id: 6,
      step: '06',
      title: 'Yayınlama & Destek',
      description: 'Projenizi yayınlıyor ve sürekli destek hizmeti sunuyoruz.',
      icon: 'ri-rocket-line',
      details: [
        'Canlı ortama çıkarma',
        'SEO optimizasyonu',
        'Performans izleme',
        'Sürekli destek'
      ]
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('süreç');
    if (section) observer.observe(section);

    // Auto-progress through steps
    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % processSteps.length);
    }, 5000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  return (
    <section id="süreç" className="py-20 bg-gradient-to-br from-gray-800 via-gray-900 to-black relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-500/3 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-cyan-500/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-blue-500/20 backdrop-blur-sm rounded-full border border-blue-400/30 mb-6">
            <i className="ri-flow-chart text-blue-400 mr-2"></i>
            <span className="text-blue-400 text-sm font-medium">Çalışma Sürecimiz</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              6 Adımda Mükemmel Sonuç
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Profesyonel yaklaşımımız ve sistematik çalışma sürecimizle 
            projelerinizi başarıya taşıyoruz.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Process Steps */}
          <div className={`space-y-4 transition-all duration-1000 transform ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
          }`}>
            {processSteps.map((step, index) => (
              <div
                key={step.id}
                onMouseEnter={() => setActiveStep(index)}
                className={`group relative bg-gradient-to-br backdrop-blur-sm rounded-2xl border p-6 transition-all duration-500 cursor-pointer transform hover:scale-105 ${
                  index === activeStep
                    ? 'from-blue-500/20 to-cyan-500/20 border-blue-400/50 shadow-2xl shadow-blue-500/20'
                    : 'from-gray-800/30 to-gray-900/30 border-gray-700/30 hover:border-gray-600/50'
                }`}
              >
                <div className="flex items-start space-x-4">
                  {/* Step Number */}
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                    index === activeStep
                      ? 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white'
                      : 'bg-gray-700/50 text-gray-400'
                  }`}>
                    {step.step}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold mb-2 transition-colors duration-300 ${
                      index === activeStep ? 'text-white' : 'text-gray-300'
                    }`}>
                      {step.title}
                    </h3>
                    <p className={`leading-relaxed transition-colors duration-300 ${
                      index === activeStep ? 'text-gray-200' : 'text-gray-400'
                    }`}>
                      {step.description}
                    </p>
                  </div>

                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300 ${
                    index === activeStep
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-gray-700/50 text-gray-500'
                  }`}>
                    <i className={`${step.icon} text-xl`}></i>
                  </div>
                </div>

                {/* Progress Line */}
                {index < processSteps.length - 1 && (
                  <div className="absolute left-14 bottom-0 w-0.5 h-4 bg-gradient-to-b from-gray-600 to-transparent"></div>
                )}

                {/* Active Indicator */}
                {index === activeStep && (
                  <div className="absolute -right-2 -top-2 w-6 h-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center animate-pulse">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Active Step Details */}
          <div className={`sticky top-24 transition-all duration-1000 delay-300 transform ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
          }`}>
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-gray-700/50 p-8">
              {/* Step Header */}
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center">
                  <i className={`${processSteps[activeStep].icon} text-3xl text-white`}></i>
                </div>
                
                <div className="text-blue-400 font-bold text-lg mb-2">
                  Adım {processSteps[activeStep].step}
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-4">
                  {processSteps[activeStep].title}
                </h3>
                
                <p className="text-gray-300 leading-relaxed">
                  {processSteps[activeStep].description}
                </p>
              </div>

              {/* Step Details */}
              <div className="space-y-3">
                <h4 className="text-lg font-semibold text-white mb-4">Bu Adımda Neler Yapıyoruz?</h4>
                {processSteps[activeStep].details.map((detail, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center space-x-3 p-3 bg-gray-800/30 rounded-lg border border-gray-700/30"
                  >
                    <div className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-blue-400 text-sm"></i>
                    </div>
                    <span className="text-gray-300">{detail}</span>
                  </div>
                ))}
              </div>

              {/* Progress Indicator */}
              <div className="mt-8 p-4 bg-gray-800/30 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-400">Süreç İlerlemesi</span>
                  <span className="text-sm text-blue-400 font-semibold">
                    {Math.round(((activeStep + 1) / processSteps.length) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all duration-1000"
                    style={{ width: `${((activeStep + 1) / processSteps.length) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
